.hero {
  position: relative;
  /* A column: the text block takes the free height, the stats bar (if any) sits under it. */
  display: flex;
  flex-direction: column;
  /* The text block below sizes and positions itself as a fraction of this, so the hero keeps
     the same proportions on every screen. Viewport units alone don't: this height is clamped,
     so on a tall monitor the box stops growing while vh keeps going. */
  --hero-h: min(86vh, 46rem);
  min-height: var(--hero-h);
  color: var(--text-on-dark);
  background: transparent;
  overflow: hidden;
  width: 100%;
  margin-inline: 0;
  /* Cancels the flow space the sticky header reserves above it (Header.module.css), so
     the video still runs full-bleed from the very top with the header floating over it. */
  margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
}
.media {
  position: absolute;
  inset: 0;
  margin: var(--sp-3);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
@media (min-width: 48rem) {
  .media {
    /* margin-inline: var(--sp-8); */
  }
}
.poster,
.video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* A little above centre: where the 2026-09-26 reel keeps its subject, so the wide,
     short hero crops mostly the bottom of the frame (the user's DevTools value). */
  object-position: center 43%;
}
/* Transparent until it is playing, then faded in over the poster: no hard cut on load. */
.video {
  opacity: 0;
  transition: opacity 700ms var(--ease);
}
.videoShowing {
  opacity: 1;
}
.poster {
  aspect-ratio: auto !important;
}
.overlay {
  position: absolute;
  inset: 0;
}
.overlay::before {
  /* A soft backdrop blur over the text side only, faded out via mask-image so the
     right side of the video stays sharp. */
  content: "";
  position: absolute;
  inset: 0;
  background: #000000cc;
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  mask-image: linear-gradient(100deg, #000 0%, #0000009e 42%, #00000000 75%);
  -webkit-mask-image: linear-gradient(100deg, #000 0%, #0000009e 42%, #00000000 75%);
}
.playPause {
  position: absolute;
  right: var(--sp-4);
  bottom: var(--sp-4);
  z-index: 2;
  display: inline-grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(16, 20, 25, 0.55);
  color: var(--text-on-dark);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
}
.playPause:hover {
  background: rgba(16, 20, 25, 0.8);
}
.playIcon {
  width: 0;
  height: 0;
  margin-left: 2px;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 10px solid currentColor;
}
.pauseIcon {
  position: relative;
  width: 11px;
  height: 12px;
}
.pauseIcon::before,
.pauseIcon::after {
  content: "";
  position: absolute;
  top: 0;
  width: 3px;
  height: 12px;
  background: currentColor;
}
.pauseIcon::before {
  left: 0;
}
.pauseIcon::after {
  right: 0;
}
@media (prefers-reduced-motion: reduce) {
  .playPause {
    display: none;
  }
}
:global(body.reduced-motion) .playPause {
  display: none;
}
.content {
  position: relative;
  /* Above `.media`, which is now a sibling painted before it. */
  z-index: 1;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Everything in the block is sized off viewport HEIGHT so the composition keeps the same
     proportions on a short laptop as on a tall monitor. With fixed pixels the text kept its
     size while the hero shrank, so it slid down over the captions burned into the video
     (which sit about three quarters of the way down every scene). The floor clears the
     floating header; the ceiling stops the block drifting on very tall screens. */
  padding-block: calc(var(--hero-h) * 0.22 + 40px) var(--sp-12);
  max-width: 52rem;
  margin-inline: auto;
}
.eyebrow {
  margin-bottom: clamp(0.4rem, calc(var(--hero-h) * 0.016), var(--sp-3));
  font-family: var(--font-display);
  font-size: var(--fs-sm);
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}
.title {
  /* Width sets the size, the hero's height caps it: a short laptop screen gets a smaller
     headline instead of one that shoves the buttons off the frame. */
  font-size: clamp(2.75rem, min(1.6rem + 3.2vw, calc(var(--hero-h) * 0.1)), var(--fs-6xl));
  line-height: 1.02;
  letter-spacing: var(--tracking-display);
  max-width: 13ch;
  color: var(--text-on-dark);
  text-wrap: balance;
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.5);
}
.lead {
  margin-top: clamp(1rem, calc(var(--hero-h) * 0.035), var(--sp-6));
  font-size: clamp(var(--fs-base), calc(var(--hero-h) * 0.024), var(--fs-lg));
  color: var(--text-on-dark);
  max-width: 34rem;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin-top: clamp(1rem, calc(var(--hero-h) * 0.04), var(--sp-8));
}
.actions :global(.btn) {
  font-size: 14px;
  font-weight: 600;
}
.actions :global(.btn-primary),
.actions :global(.btn-primary):hover {
  background: #fd7f02;
  color: #000000;
}
.actions :global(.btn-primary):hover {
  filter: brightness(1.1);
}
.actions :global(.btn-secondary) {
  background: #ffffff;
  border-color: #ffffff;
  color: #000000;
}
.actions :global(.btn-secondary):hover {
  background: var(--asphalt-300);
  border-color: var(--asphalt-300);
  color: #000000;
}
/* ---- stats bar: the credibility figures, docked on the frame's bottom edge ---- */
.statsBar {
  position: relative;
  z-index: 1;
  /* Inside the rounded media frame, which is inset by --sp-3 on every side. */
  margin: 0 var(--sp-3) var(--sp-3);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 7rem), max-content));
  gap: var(--sp-4) clamp(var(--sp-8), 5vw, var(--sp-20));
  padding-block: var(--sp-5);
}
.stat {
  display: grid;
  gap: 2px;
}
.statValue {
  font-family: var(--font-display);
  font-size: clamp(var(--fs-xl), 1rem + 1.2vw, var(--fs-3xl));
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: var(--tracking-heading);
  color: var(--text-on-dark);
}
.statLabel {
  font-size: var(--fs-sm);
  color: var(--text-on-dark-muted);
}
@media (prefers-reduced-motion: reduce) {
  .video {
    display: none;
  }
}
:global(body.reduced-motion) .video {
  display: none;
}
@media (min-width: 64rem) {
  /* `.hero .content`, not `.content`: the element also has the global `.container`, whose
     `margin-inline: auto` has the same specificity, and the production CSS bundle loads it
     after this module, which centered the text on the deployed site. */
  .hero .content {
    margin-inline: 0;
    padding-left: var(--sp-8);
  }
  /* Lines the figures up with the text block above (its padding, minus the bar's inset). */
  .hero .stats {
    margin-inline: 0;
    padding-left: calc(var(--sp-8) - var(--sp-3));
  }
  /* Floor sized off viewport HEIGHT, not an aspect-ratio tied to width. An aspect-ratio
     box scales purely with how wide the screen is, so a 24" monitor and a narrower
     laptop — even at similar heights — ended up with very different leftover space
     below the text, since the box's height had no relationship to the actual vertical
     room available. This is a `min-height`, not a fixed `height`, so tall content still
     grows the box (`.content` is in flow, outside the absolute `.media`) — 34.6rem is
     a floor for short screens where 84.2vh alone would be too short for the headline, and
     the 51.8rem cap keeps very tall monitors from getting an oversized hero.
     `object-fit: cover` on `.video`/`.poster` (unconditional, further up) crops to fill
     whatever height this resolves to, so nothing here depends on matching the video's
     own aspect ratio. */
  .hero {
    --hero-h: max(34.6rem, min(84.2vh, 51.8rem));
    min-height: var(--hero-h);
  }
}
@media (max-width: 47.99rem) {
  /* One column: the text runs across the whole width, so shade top-down instead of left-to-right.
     Dark enough behind the headline, clearing by the bottom so the video is still visible —
     a flat wash over everything plus the blurred panel below turned the whole frame grey. */
  .overlay {
    background: linear-gradient(
      180deg,
      rgba(11, 14, 18, 0.88) 0%,
      rgba(11, 14, 18, 0.72) 48%,
      rgba(11, 14, 18, 0.46) 78%,
      rgba(11, 14, 18, 0.12) 100%
    );
  }
  /* The blurred scrim is aimed at the left half of a wide frame; in one column it just
     fogs the whole video. */
  .overlay::before {
    display: none;
  }
  /* The three figures on one line, as on wider screens: each value stays whole and its
     label wraps under it, rather than the third figure dropping to a row of its own. */
  .stats {
    display: flex;
    /* Top-aligned: stretched to the tallest label, a short one's text slid down its column. */
    align-items: start;
    gap: var(--sp-3);
  }
  /* Equal columns; a value wider than its share ("Since 2005") keeps the width it needs.
     Each figure is centred over its label, which wraps in a column this narrow. */
  .stat {
    flex: 1 1 0;
    justify-items: center;
    text-align: center;
  }
  .statValue {
    font-size: clamp(1.0625rem, 4.8vw, var(--fs-xl));
    white-space: nowrap;
  }
  .statLabel {
    font-size: 0.75rem;
  }
  /* That line takes the bar's whole width, so the video's pause button moves up to the
     frame's top corner, just under the floating header. */
  .playPause {
    top: calc(var(--header-margin) + var(--header-h));
    bottom: auto;
  }
}
